package com.macrofocus.high_d.parallelcoordinates

import com.macrofocus.common.command.AbstractUICommand
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.properties.PropertyListener
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.hierarchy.HierarchyEvent
import com.macrofocus.hierarchy.HierarchyListener
import com.macrofocus.high_d.axis.AbstractAxisView
import com.macrofocus.high_d.axis.Alignment
import com.macrofocus.high_d.axis.AxisListener
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.high_d.axis.AxisView
import com.macrofocus.high_d.axis.DefaultAxisView
import com.macrofocus.high_d.axis.group.AxisGroupModel
import com.macrofocus.high_d.axis.group.AxisGroupView
import com.macrofocus.high_d.parallelcoordinates.geometry.Geometry
import com.macrofocus.high_d.parallelcoordinates.layout.DefaultParallelCoordinatesLayoutEngine
import com.macrofocus.high_d.parallelcoordinates.layout.ParallelCoordinatesLayout
import com.macrofocus.high_d.parallelcoordinates.layout.ParallelCoordinatesLayoutEngine
import com.macrofocus.license.LicenseModel
import org.mkui.graphics.colortheme.ColorTheme
import com.macrofocus.order.MutableVisibleOrder
import com.macrofocus.order.OrderEvent
import com.macrofocus.order.OrderListener
import org.mkui.canvas.Rendering
import org.mkui.canvas.MouseEvent
import org.mkui.canvas.MouseListener
import org.mkui.component.CPComponent
import org.mkui.component.CPInteractiveComponent
import org.mkui.component.absolute.AbsoluteCPContainer
import org.mkui.component.absolute.Layout
import org.mkui.component.menu.CPPopupMenu
import org.mkui.geom.Rectangle2D
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min

/**
 * The parallel coordinates, after SwingParallelCoordinatesView: one plotting component per level of the axis
 * hierarchy, with the header, the range slider and the minimum and maximum labels of every axis laid out above it by
 * the [DefaultParallelCoordinatesLayoutEngine]. Dragging a header reorders the axes (see
 * [DefaultParallelCoordinatesController]), double-clicking it inverts the axis and its popup trigger opens the menu of
 * ParallelCoordinatesPopupMenu; dragging the handles or the range of a slider filters the rows. The groups of axes
 * have a collapsible header, the minimum and maximum of the numerical axes can be edited, and with
 * [getAllowScrolling] the axes keep the width of [getColumnSize] and the view scrolls horizontally when they do not fit.
 */
class DefaultParallelCoordinatesView<Row, Column> : AbstractParallelCoordinatesView<Row, Column>() {
    override val component: AbsoluteCPContainer = AbsoluteCPContainer(object : Layout {
        override fun layout(container: AbsoluteCPContainer, width: Double, height: Double) {
            runLayout()
        }
    })

    private val axisListener: AxisListener = object : AxisListener {
        override fun axisChanged() {
            refresh()
        }
    }

    private val parallelCoordinatesListener = object : ParallelCoordinatesListener {
        override fun pararallelCoordinatesChanged() {
            reset()
        }
    }

    private val axisGroupHierarchyListener = object : HierarchyListener<AxisGroupModel<Row, Column>> {
        override fun hierarchyNodeInserted(event: HierarchyEvent<AxisGroupModel<Row, Column>>) = groupsChanged()

        override fun hierarchyNodeChanged(event: HierarchyEvent<AxisGroupModel<Row, Column>>) = groupsChanged()

        override fun hierarchyNodeRemoved(event: HierarchyEvent<AxisGroupModel<Row, Column>>) = groupsChanged()

        override fun hierarchyStructureChanged(event: HierarchyEvent<AxisGroupModel<Row, Column>>) = groupsChanged()
    }

    /** The axis orders of the groups listened to, for the axes added to, moved in or removed from any group. */
    private val listenedOrders = HashSet<MutableVisibleOrder<AxisModel<Row, Column>>>()

    private fun listenToOrders() {
        val model = model ?: return
        for (axisGroup in model.getAxisHierarchy().axisGroupHierarchy.breadthFirstIterator()) {
            val order = axisGroup.axisOrder ?: continue
            if (listenedOrders.add(order)) {
                order.addOrderListener(orderListener)
            }
        }
    }

    private fun groupsChanged() {
        listenToOrders()
        refresh()
    }

    private val orderListener = object : OrderListener<AxisModel<Row, Column>> {
        override fun orderChanged(event: OrderEvent<AxisModel<Row, Column>>?) = refresh()

        override fun orderVisibility(event: OrderEvent<AxisModel<Row, Column>>) = refresh()

        override fun orderAdded(event: OrderEvent<AxisModel<Row, Column>>) = refresh()

        override fun orderRemoved(event: OrderEvent<AxisModel<Row, Column>>) = refresh()
    }

    private val alignmentListener = object : PropertyListener<Alignment> {
        override fun propertyChanged(event: PropertyEvent<Alignment>) {
            for (components in axisComponents.values) {
                (components.getLabel() as? AxisHeaderView<*, *>)?.alignment = event.newValue
            }
            refresh()
        }
    }

    /**
     * Whether the axes have a range slider and labels and their headers react to the mouse. Without, as in the
     * parallel coordinates matrix, only the names of the axes are shown above the plot.
     */
    var axisControls: Boolean = true
        set(value) {
            if (field != value) {
                field = value
                for (components in axisComponents.values) {
                    (components.getLabel() as? AxisHeaderView<*, *>)?.isInteractive = value
                }
                refresh()
            }
        }

    private val refreshListener = object : PropertyListener<Any?> {
        override fun propertyChanged(event: PropertyEvent<Any?>) {
            refresh()
        }
    }

    private val colorThemeListener = object : PropertyListener<ColorTheme> {
        override fun propertyChanged(event: PropertyEvent<ColorTheme>) {
            runLayout(relayoutPlots = false)
        }
    }

    private val collapsedListener = object : PropertyListener<Boolean> {
        override fun propertyChanged(event: PropertyEvent<Boolean>) {
            refresh()
        }
    }

    init {
        getAlignment().addPropertyListener(alignmentListener)
        @Suppress("UNCHECKED_CAST")
        (getAllowScrolling() as MutableProperty<Any?>).addPropertyListener(refreshListener)
        @Suppress("UNCHECKED_CAST")
        (getColumnSize() as MutableProperty<Any?>).addPropertyListener(refreshListener)
        getColorTheme().addPropertyListener(colorThemeListener)
    }

    // Horizontal scrolling

    /** Left of the viewport in the laid out content, which is wider than the view while scrolling. */
    private var scrollX = 0.0

    private val scrollBar: HorizontalScrollBarView by lazy {
        HorizontalScrollBarView {
            scrollX = it
            runLayout(relayoutPlots = false)
        }.also { component.addComponent(it, DRAG_LAYER) }
    }

    /** Width of the axes at the column size, the collapsed groups taking 4 pixels per axis as in the layout. */
    private fun preferredWidth(): Double {
        val model = model ?: return 0.0
        val columnSize = getColumnSize().value ?: return 0.0
        var width = 0.0
        for (axisGroup in model.getAxisHierarchy().axisGroupHierarchy.leavesIterator()) {
            width += axisGroup.visibleAxisCount * if (axisGroup.isCollapsed) 4.0 else columnSize.toDouble()
        }
        return width
    }

    /** Whether the axes are wider than the view and it scrolls. */
    private val isScrolling: Boolean
        get() = getAllowScrolling().value == true && preferredWidth() > component.width

    /** Places [child] at [bounds] of the content, shifted by the scrolling. */
    private fun place(child: CPComponent, bounds: Rectangle2D) {
        component.setBounds(child, Rectangle2D.Double(bounds.x - scrollX, bounds.y, bounds.width, bounds.height))
    }

    fun runLayout() {
        runLayout(relayoutPlots = true)
    }

    /**
     * Lays out the components; the plots are only moved, keeping their drawings, unless [relayoutPlots] (when their
     * size or the axes may have changed).
     */
    private fun runLayout(relayoutPlots: Boolean) {
        // Adding the components created while laying out lays out the container again
        if (inLayout) {
            return
        }
        inLayout = true
        try {
            doRunLayout(relayoutPlots)
        } finally {
            inLayout = false
        }
    }

    private var inLayout = false

    private fun doRunLayout(relayoutPlots: Boolean) {
        val model = model ?: return
        if (component.width <= 0 || component.height <= 0) {
            return
        }
        val scrolling = isScrolling
        if (scrolling) {
            val viewportHeight = component.height - HorizontalScrollBarView.HEIGHT
            scrollBar.setExtents(preferredWidth(), component.width)
            scrollX = scrollBar.value
            component.setBounds(scrollBar, Rectangle2D.Double(0.0, viewportHeight, component.width, HorizontalScrollBarView.HEIGHT))
            component.setVisible(scrollBar, true)
        } else {
            scrollX = 0.0
            if (scrollBarCreated) {
                component.setVisible(scrollBar, false)
            }
        }
        scrollBarCreated = scrollBarCreated || scrolling
        if (getWidth() <= 0 || getHeight() <= 0) {
            return
        }
        val axisView: AxisView<Row, Column> = createDummyAxisView()
        val layout = (if (axisControls) layoutEngine.doLayout(axisView.preferredWidth, axisView.beforeTrackGap, axisView.afterTrackGap)
            else layoutEngine.doLayout(axisView.preferredWidth, 0, 0)) ?: return
        this.layout = layout

        for (l in 0 until layout.getLevelCount()) {
            val bounds: Rectangle2D = layout.getLevelBounds(l)
            val component: ParallelCoordinatesComponent<Row, Column> = getParallelCoordinatesComponent(l)!!
            if (relayoutPlots) {
                component.clearCache()
            }
            place(component, bounds)
            if (relayoutPlots) {
                component.scheduleUpdate()
            }
        }

        // The components of the hidden axes, and of the groups in collapsed ones, are not shown
        val laidOut = HashSet<AxisModel<Row, Column>>()
        val laidOutGroups = HashSet<AxisGroupModel<Row, Column>>()
        val hierarchy = model.getAxisHierarchy().axisGroupHierarchy
        for (axisGroup in hierarchy.breadthFirstIterator()) {
            var parent = hierarchy.getParent(axisGroup)
            while (parent != null && !parent.isCollapsed) {
                parent = hierarchy.getParent(parent)
            }
            if (parent == null) {
                laidOutGroups.add(axisGroup)
                doLayout(layout, axisGroup, laidOut)
            }
        }
        for ((axisModel, components) in axisComponents) {
            if (axisModel !in laidOut) {
                for (c in components.components()) {
                    component.setVisible(c, false)
                }
            }
        }
        for ((axisGroup, axisGroupView) in axisGroupViews) {
            if (axisGroup !in laidOutGroups) {
                component.setVisible(axisGroupView, false)
            }
        }
    }

    private var scrollBarCreated = false

    protected fun createDummyAxisView(): AxisView<Row, Column> {
        return DefaultAxisView()
    }

    private fun doLayout(layout: ParallelCoordinatesLayout<Row, Column>, axisGroup: AxisGroupModel<Row, Column>, laidOut: MutableSet<AxisModel<Row, Column>>) {
        val axisGroupView: AxisGroupView? = getAxisGroupView(axisGroup)
        if (axisGroupView != null) {
            place(axisGroupView, layout.getHeaderBounds(axisGroup))
            component.setVisible(axisGroupView, true)
        }

        if (axisGroup.isCollapsed) {
            return
        }
        for (axisModel in axisGroup.axisOrder!!) {
            laidOut.add(axisModel)
            val components = getParallelPanel(axisModel)
            install(components)
            val axisView: AxisView<Row, Column> = components.getAxisView()
            val headerBounds = layout.getHeaderBounds(axisGroup, axisModel)!!
            val label = components.getLabel()
            if (label != null) {
                place(label, headerBounds)
                this.component.setVisible(label, true)
            }

            // The extremes of the range of the slider at those of the track
            val sliderBounds = layout.getSliderBounds(axisGroup, axisModel)!!
            val inset = (axisView.beforeTrackGap - SLIDER_MARGIN).toDouble()
            place(axisView, Rectangle2D.Double(sliderBounds.x, sliderBounds.y + inset, sliderBounds.width, sliderBounds.height - 2 * inset))
            this.component.setVisible(axisView, axisControls && components.isSliderVisible)
            layoutRangeBubbles(components, axisModel, sliderBounds, axisView)

            // The minimum and maximum next to the track, up to the slider of the next axis
            val axisBounds = layout.getBounds(axisGroup, axisModel)
            val x = sliderBounds.x + axisView.preferredWidth
            val width = min(axisBounds.width - axisView.preferredWidth, max(headerBounds.maxX, axisBounds.maxX - axisView.preferredWidth / 2.0) - x)
            val maxLabel = components.getMaxLabel()
            val minLabel = components.getMinLabel()
            if (maxLabel != null && minLabel != null) {
                val labelsVisible = axisControls && components.isLabelsVisible && width > 0
                if (labelsVisible) {
                    place(maxLabel, Rectangle2D.Double(x, sliderBounds.y, width, axisView.beforeTrackGap.toDouble()))
                    place(minLabel, Rectangle2D.Double(x, sliderBounds.maxY - axisView.afterTrackGap, width, axisView.afterTrackGap.toDouble()))
                    // In the text color of the current color theme
                    (maxLabel as? AxisLimitView<*, *>)?.update()
                    (minLabel as? AxisLimitView<*, *>)?.update()
                }
                this.component.setVisible(maxLabel, labelsVisible)
                this.component.setVisible(minLabel, labelsVisible)
            }
        }
    }

    /**
     * The values of the range next to the handles of the slider (only those not at the extremes of the axis) while the
     * mouse is over it, as PopupAxisController.showSliderValues.
     */
    private fun layoutRangeBubbles(components: DefaultAxisComponents<Row, Column>, axisModel: AxisModel<Row, Column>, sliderBounds: Rectangle2D, axisView: AxisView<Row, Column>) {
        val endBubble = components.endBubble ?: return
        val startBubble = components.startBubble ?: return
        val interval = axisModel.interval
        val scaled = axisModel.scaledInterval
        val shown = axisControls && components.isSliderHovered && components.isSliderVisible
        val range = scaled.maximum - scaled.minimum
        val top = sliderBounds.y + axisView.beforeTrackGap
        val trackHeight = sliderBounds.height - axisView.beforeTrackGap - axisView.afterTrackGap
        fun y(value: Double) = top + if (range != 0.0) trackHeight * (1 - (value - scaled.minimum) / range) else trackHeight
        val x = sliderBounds.maxX
        val endShown = shown && interval.end != interval.maximum
        if (endShown) {
            endBubble.text = axisModel.getFormattedCeilValue(interval.end)
            place(endBubble, Rectangle2D.Double(x, y(scaled.end) - RangeBubbleView.HEIGHT, RangeBubbleView.WIDTH, RangeBubbleView.HEIGHT))
        }
        component.setVisible(endBubble, endShown)
        val startShown = shown && interval.start != interval.minimum
        if (startShown) {
            startBubble.text = axisModel.getFormattedFloorValue(interval.start)
            place(startBubble, Rectangle2D.Double(x, y(scaled.start), RangeBubbleView.WIDTH, RangeBubbleView.HEIGHT))
        }
        component.setVisible(startBubble, startShown)
    }

    private fun install(components: DefaultAxisComponents<Row, Column>) {
        // The headers above the sliders above the labels, all above the plot
        components.getMaxLabel()?.let { component.addComponent(it, PALETTE_LAYER) }
        components.getMinLabel()?.let { component.addComponent(it, PALETTE_LAYER) }
        component.addComponent(components.getAxisView(), PALETTE_LAYER)
        components.getLabel()?.let { component.addComponent(it, MODAL_LAYER) }
        components.endBubble?.let { component.addComponent(it, POPUP_LAYER) }
        components.startBubble?.let { component.addComponent(it, POPUP_LAYER) }
    }

    fun getLabel(axisModel: AxisModel<Row, Column>): CPComponent? = getParallelPanel(axisModel).getLabel()

    fun getAndInstallAxisView(axisModel: AxisModel<Row, Column>): AxisView<Row, Column> {
        val components = getParallelPanel(axisModel)
        install(components)
        return components.getAxisView()
    }

    fun getParallelPanel(axisModel: AxisModel<Row, Column>): DefaultAxisComponents<Row, Column> {
        return axisComponents.getOrPut(axisModel) {
            axisModel.addAxisListener(axisListener)
            val theme = { getColorTheme().value }
            val axisView = createAxisView(axisModel)
            val components = DefaultAxisComponents(
                createAxisHeaderView(axisModel),
                axisView,
                AxisLimitView(axisModel, maximum = true, theme),
                AxisLimitView(axisModel, maximum = false, theme),
                RangeBubbleView(),
                RangeBubbleView(),
            )
            // The values of the range are shown while the mouse is over the slider
            ((axisView as? AbstractAxisView<*, *>)?.rangeSliderView as? CPInteractiveComponent)?.addMouseListener(object : MouseListener {
                override fun mouseEntered(e: MouseEvent) {
                    components.isSliderHovered = true
                    runLayout()
                }

                override fun mouseExited(e: MouseEvent) {
                    components.isSliderHovered = false
                    runLayout()
                }

                override fun mouseClicked(e: MouseEvent) {
                }

                override fun mousePressed(e: MouseEvent) {
                }

                override fun mouseReleased(e: MouseEvent) {
                }
            })
            components
        }
    }

    protected fun createAxisHeaderView(axisModel: AxisModel<Row, Column>): CPComponent {
        return AxisHeaderView(this, axisModel).also {
            it.alignment = getAlignment().value
            it.isInteractive = axisControls
        }
    }

    protected fun createAxisView(axisModel: AxisModel<Row, Column>?): AxisView<Row, Column> {
        return DefaultAxisView(axisModel)
    }

    /** Converts the horizontal location [x] of an event of [child] to the coordinates of the view. */
    fun toViewX(child: CPComponent, x: Int): Int = x + (component.getBounds(child)?.x?.toInt() ?: 0)

    /** Unselects the headers of the axes other than [axisModel]. */
    fun deselectOtherHeaders(axisModel: AxisModel<*, *>) {
        for (other in axisComponents.keys) {
            if (other !== axisModel) {
                other.buttonModel.value = false
            }
        }
    }

    // Tooltips of the headers

    private val tooltip: TooltipView by lazy { TooltipView().also { component.addComponent(it, DRAG_LAYER) } }
    private var tooltipOwner: CPComponent? = null

    /** Shows [text], [textWidth] pixels wide, in a tooltip under ([x], [y]) of [header] (above if there is no room). */
    fun showTooltip(header: CPComponent, text: String, textWidth: Double, x: Int, y: Int) {
        val bounds = component.getBounds(header) ?: return
        tooltip.text = text
        val width = min(textWidth + 2 * TooltipView.PADDING + 1, component.width)
        val left = max(0.0, min(bounds.x + x, component.width - width))
        var top = bounds.y + y + TOOLTIP_OFFSET
        if (top + TooltipView.HEIGHT > component.height) {
            top = bounds.y + y - TooltipView.HEIGHT - 4
        }
        component.setBounds(tooltip, Rectangle2D.Double(left, top, width, TooltipView.HEIGHT))
        component.setVisible(tooltip, true)
        tooltipOwner = header
    }

    /** Hides the tooltip of [header], if shown. */
    fun hideTooltip(header: CPComponent) {
        if (tooltipOwner === header) {
            component.setVisible(tooltip, false)
            tooltipOwner = null
        }
    }

    /** Opens the popup menu of the selected axes at ([x], [y]) of [header]. */
    fun showHeaderPopupMenu(header: CPComponent, x: Int, y: Int) {
        val model = model ?: return
        val bounds = component.getBounds(header) ?: return
        createHeaderPopupMenu(model).show(component, x + bounds.x.toInt(), y + bounds.y.toInt())
    }

    /** The items of ParallelCoordinatesPopupMenu, acting on the selected axes. */
    fun createHeaderPopupMenu(model: ParallelCoordinatesModel<Row, Column>): CPPopupMenu {
        val selected = model.getSelectedAxis().toList()
        val menu = CPPopupMenu()
        fun item(title: String, enabled: Boolean = true, action: () -> Unit) {
            menu.add(object : AbstractUICommand(SimpleProperty<String?>(title), SimpleProperty<Boolean?>(enabled)) {
                override fun execute(source: Any?) {
                    action()
                }
            })
        }

        fun separator() = menu.add(null)

        fun fullRangeAfter(axisModel: AxisModel<Row, Column>, change: () -> Unit) {
            val fullRange = axisModel.interval.isFullRange
            change()
            if (fullRange) {
                axisModel.interval.setValue(axisModel.minimum, axisModel.maximum - axisModel.minimum)
            }
        }

        item("Hide") { selected.forEach { model.setHidden(it, true) } }
        item("Show all hidden") { model.unhideAllAxis() }
        separator()
        item("Invert") {
            for (axisModel in selected) {
                val min = axisModel.maximum
                val max = axisModel.minimum
                val start = axisModel.interval.start
                val end = axisModel.interval.end
                axisModel.setMinMax(min, max)
                axisModel.interval.setValue(end, start - end)
            }
        }
        item("Show full range", enabled = selected.any {
            val statistics = it.columnStatistics
            it.isNumerical && it.interval.maximum != statistics.max?.toDouble() || it.interval.minimum != statistics.min?.toDouble()
        }) {
            for (axisModel in selected) {
                val statistics = axisModel.columnStatistics
                val min = statistics.min?.toDouble() ?: continue
                val max = statistics.max?.toDouble() ?: continue
                fullRangeAfter(axisModel) { axisModel.setMinMax(min, max) }
            }
        }
        item("Show only current range", enabled = selected.any { !it.interval.isFullRange }) {
            for (axisModel in selected) {
                axisModel.setMinMax(axisModel.interval.start, axisModel.interval.end)
            }
        }
        item("Center on zero", enabled = selected.any {
            val statistics = it.columnStatistics
            it.isNumerical && (statistics.min?.toDouble() ?: 0.0) < 0 && (statistics.max?.toDouble() ?: 0.0) > 0
        }) {
            for (axisModel in selected) {
                val statistics = axisModel.columnStatistics
                val max = max(abs(statistics.min?.toDouble() ?: 0.0), abs(statistics.max?.toDouble() ?: 0.0))
                fullRangeAfter(axisModel) { axisModel.setMinMax(-max, max) }
            }
        }
        item("Align on mean") {
            for (axisModel in selected) {
                val statistics = axisModel.columnStatistics
                val min = statistics.min?.toDouble() ?: continue
                val max = statistics.max?.toDouble() ?: continue
                val range = max(statistics.mean - min, max - statistics.mean)
                fullRangeAfter(axisModel) { axisModel.setMinMax(statistics.mean - range, statistics.mean + range) }
            }
        }
        item("Reset range") {
            for (axisModel in selected) {
                val statistics = axisModel.columnStatistics
                val min = statistics.min?.toDouble() ?: continue
                val max = statistics.max?.toDouble() ?: continue
                axisModel.setMinMax(min, max)
                axisModel.interval.setValue(min, max - min)
            }
        }
        separator()
        val components = selected.map { getParallelPanel(it) }
        val sliderVisible = components.all { it.isSliderVisible }
        val labelsVisible = components.all { it.isLabelsVisible }
        item(if (sliderVisible) "Hide range slider" else "Show range slider") {
            components.forEach { it.isSliderVisible = !sliderVisible }
            runLayout()
        }
        item(if (labelsVisible) "Hide labels" else "Show labels") {
            components.forEach { it.isLabelsVisible = !labelsVisible }
            runLayout()
        }
        return menu
    }

    // Axis reordering, called by the controller

    private var draggedAxis: AxisModel<Row, Column>? = null
    private var draggedLocation = 0.0

    /** Starts dragging the axis of [axisView], whose location then follows [dragAxis]. */
    fun startAxisDrag(axisView: AxisView<*, *>) {
        @Suppress("UNCHECKED_CAST")
        val axisModel = axisView.model as AxisModel<Row, Column>? ?: return
        val bounds = component.getBounds(axisView) ?: return
        draggedAxis = axisModel
        draggedLocation = bounds.x + scrollX
    }

    /** Moves the dragged axis by [shift] pixels from where the drag started, reordering the axes it passes. */
    fun dragAxis(axisView: AxisView<*, *>, shift: Int) {
        val model = model ?: return
        val axisModel = draggedAxis ?: return
        if (axisView.model !== axisModel) {
            return
        }
        val axisGroup = model.getAxisHierarchy().getAxisGroup(axisModel) ?: return
        val layout = layout ?: return
        val rect = layout.getBounds(axisGroup)
        val alignment = getAlignment().value
        val center = draggedLocation + shift + axisView.preferredWidth / 2.0
        val location = min(alignment.getMaximumPosition(axisGroup) + 0.05, max(alignment.getMinimumPosition(axisGroup) - 0.05, (center - rect.x) / rect.width))
        model.getAxisLocations(axisGroup)?.drag(alignment, axisModel, location)
        refresh()
    }

    /** Ends the drag of the axis, which goes to its place in the order. */
    fun stopAxisDrag(axisView: AxisView<*, *>) {
        val model = model ?: return
        val axisModel = draggedAxis ?: return
        draggedAxis = null
        val axisGroup = model.getAxisHierarchy().getAxisGroup(axisModel) ?: return
        model.getAxisLocations(axisGroup)?.stopDragging()
        refresh()
    }

    override var model: ParallelCoordinatesModel<Row, Column>? = null
        get() = field
        set(value) {
            val old = field
            if (old != null) {
                old.removeParallelCoordinateListener(parallelCoordinatesListener)
                val groupHierarchy = old.getAxisHierarchy().axisGroupHierarchy
                groupHierarchy.removeHierarchyListener(axisGroupHierarchyListener)
            }
            for (order in listenedOrders) {
                order.removeOrderListener(orderListener)
            }
            listenedOrders.clear()

            field = value

            tooltipOwner?.let { hideTooltip(it) }
            for ((axisGroup, axisGroupView) in axisGroupViews) {
                axisGroup.collapsedProperty().removePropertyListener(collapsedListener)
                component.setVisible(axisGroupView, false)
                (axisGroupView as? AxisGroupHeaderView)?.dispose()
            }
            axisGroupViews.clear()
            for ((axisModel, components) in axisComponents) {
                axisModel.removeAxisListener(axisListener)
                for (c in components.components()) {
                    component.setVisible(c, false)
                }
                components.dispose()
            }
            axisComponents.clear()
            for (component in parallelCoordinatesComponents.values) {
                component.model = null
            }
            parallelCoordinatesComponents.clear()
            layout = null

            if (value != null) {
                value.addParallelCoordinatesListener(parallelCoordinatesListener)
                val groupHierarchy = value.getAxisHierarchy().axisGroupHierarchy
                groupHierarchy.addHierarchyListener(axisGroupHierarchyListener)
                listenToOrders()

                for (groupModel in groupHierarchy.breadthFirstIterator()) {
                    for (axisModel in groupModel.axisOrder!!) {
                        getParallelPanel(axisModel)
                    }
                }
            }

            refresh()
        }

    private val axisComponents: MutableMap<AxisModel<Row, Column>, DefaultAxisComponents<Row, Column>> = LinkedHashMap()
    private val axisGroupViews: MutableMap<AxisGroupModel<Row, Column>, AxisGroupView> = HashMap()
    private val parallelCoordinatesComponents: MutableMap<Int, ParallelCoordinatesComponent<Row, Column>> = HashMap()

    private var isSelectionMode = true

    private val headerMaximumHeight = AxisHeaderView.HEIGHT

    private companion object {
        /** Distance between the ends of an AbstractRangeSliderView and the extremes of its range. */
        const val SLIDER_MARGIN = 8

        /** Distance between the mouse and the top of a tooltip under it. */
        const val TOOLTIP_OFFSET = 20.0
    }

    private val layoutEngine: ParallelCoordinatesLayoutEngine<Row, Column> = DefaultParallelCoordinatesLayoutEngine<Row, Column>(this)
    private var layout: ParallelCoordinatesLayout<Row, Column>? = null

    override fun getClosestRow(x: Int, y: Int): Row? {
        val layout = layout ?: return null
        for (l in 0 until layout.getLevelCount()) {
            val component: ParallelCoordinatesComponent<Row, Column> = getParallelCoordinatesComponent(l)!!
            val b: Rectangle2D = this.component.getBounds(component) ?: continue
            if (b.contains(x.toDouble(), y.toDouble())) {
                return component.getClosestRow(x - b.x.toInt(), y - b.y.toInt())
            }
        }
        return null
    }

    private fun getParallelCoordinatesComponent(level: Int): ParallelCoordinatesComponent<Row, Column>? {
        if (!parallelCoordinatesComponents.containsKey(level)) {
            val parallelCoordinatesComponent: ParallelCoordinatesComponent<Row, Column> = DefaultParallelCoordinatesComponent<Row, Column>(this, level)
            parallelCoordinatesComponent.model = model
            parallelCoordinatesComponents.put(level, parallelCoordinatesComponent)
            component.addComponent(parallelCoordinatesComponent, DEFAULT_LAYER)
        }
        return parallelCoordinatesComponents[level]
    }

    override fun getRows(rect: Rectangle2D?): Collection<Row>? {
        val layout = layout ?: return null
        val list: MutableSet<Row> = HashSet()
        for (l in 0 until layout.getLevelCount()) {
            val component: ParallelCoordinatesComponent<Row, Column> = getParallelCoordinatesComponent(l)!!
            val b: Rectangle2D = this.component.getBounds(component) ?: continue
            val cb: Rectangle2D = Rectangle2D.Double(b.x, b.y, b.width, b.height)
            if (cb.intersects(rect!!)) {
                val r: Rectangle2D = Rectangle2D.Double(rect.x - b.x, rect.y - b.y, rect.width, rect.height)
                val rows: List<Row>? = component.getRows(r)
                if (rows != null) {
                    list.addAll(rows)
                }
            }
        }
        return list
    }

    override fun isSelectionMode(): Boolean {
        return isSelectionMode
    }

    override fun setSelectionMode(value: Boolean) {
        isSelectionMode = value
    }

    /** Width of the laid out content, wider than the view while it scrolls. */
    override fun getWidth(): Int {
        return (if (isScrolling) preferredWidth() else component.width).toInt()
    }

    /** Height of the laid out content, above the scroll bar while scrolling. */
    override fun getHeight(): Int {
        return (component.height - if (isScrolling) HorizontalScrollBarView.HEIGHT else 0.0).toInt()
    }

    override fun isShowTiming(): Boolean {
        return properties.getProperty(ParallelCoordinatesView.PropertyType.ShowTiming).value as Boolean
    }

    override fun setShowTiming(showTiming: Boolean) {
        properties.getProperty(ParallelCoordinatesView.PropertyType.ShowTiming).value = showTiming
    }

    /** The header of [axisGroup], created on demand for the groups below the root when the axes have controls. */
    override fun getAxisGroupView(axisGroup: AxisGroupModel<Row, Column>?): AxisGroupView? {
        if (axisGroup == null || !axisControls) {
            return null
        }
        val model = model ?: return null
        if (model.getAxisHierarchy().axisGroupHierarchy.isRoot(axisGroup)) {
            return null
        }
        axisGroupViews[axisGroup]?.let { return it }
        val axisGroupView = AxisGroupHeaderView(axisGroup)
        axisGroupViews[axisGroup] = axisGroupView
        axisGroup.collapsedProperty().addPropertyListener(collapsedListener)
        component.addComponent(axisGroupView, MODAL_LAYER)
        return axisGroupView
    }

    override fun getAxisView(axisModel: AxisModel<*, *>?): AxisView<Row, Column>? {
        @Suppress("UNCHECKED_CAST")
        return axisComponents[axisModel as AxisModel<Row, Column>?]?.getAxisView()
    }

    override fun setLicenseModel(licenseModel: LicenseModel?) {
    }

    override fun waitUntilReady() {
        for (parallelCoordinatesComponent in parallelCoordinatesComponents.values) {
            parallelCoordinatesComponent.waitUntilReady()
        }
    }

    override fun getHeaderAxisGroupMaximumHeight(): Int {
        return headerMaximumHeight
    }

    override fun getHeaderAxisMaximumHeight(): Int {
        return headerMaximumHeight
    }

    override fun getParallelCoordinatesLayout(): ParallelCoordinatesLayout<Row, Column> {
        return layout!!
    }

    override fun getAxisX(axisGroup: AxisGroupModel<Row, Column>, axisModel: AxisModel<Row, Column>): Int {
        return layout!!.getTrackBounds(axisGroup, axisModel).x.toInt()
    }

    override fun setShowFiltered(showFiltered: MutableProperty<Boolean>?) {
        properties.replaceProperty(ParallelCoordinatesView.PropertyType.ShowFiltered, showFiltered!!)

        createOverPlots()
    }

    override fun setRendering(rendering: MutableProperty<Rendering>?) {
        if (getRendering() !== rendering) {
            if (getRendering() != null) {
                getRendering().removePropertyListener(renderingListener)
            }

            properties.replaceProperty(ParallelCoordinatesView.PropertyType.Rendering, rendering!!)

            if (getRendering() != null) {
                getRendering().addPropertyListener(renderingListener)
            }
        }

        createOverPlots()
    }

    override fun setAntialiasing(antialiasing: MutableProperty<Boolean>?) {
        properties.replaceProperty(ParallelCoordinatesView.PropertyType.Antialiasing, antialiasing!!)

        createOverPlots()
    }

    override fun setGeometry(geometry: MutableProperty<Geometry>) {
        if (getGeometry() !== geometry) {
            if (getGeometry() != null) {
                getGeometry().removePropertyListener(geometryListener)
            }

            properties.replaceProperty(ParallelCoordinatesView.PropertyType.Geometry, geometry)

            if (getGeometry() != null) {
                getGeometry().addPropertyListener(geometryListener)
            }
        }

        refresh()
    }

    private val renderingListener: PropertyListener<Rendering> =
        object : PropertyListener<Rendering> {
            override fun propertyChanged(event: PropertyEvent<Rendering>) {
                createOverPlots()
            }
        }

    private val geometryListener: PropertyListener<Geometry> = object : PropertyListener<Geometry> {
        override fun propertyChanged(event: PropertyEvent<Geometry>) {
            refresh()
        }
    }

    private fun createOverPlots() {
        for (parallelCoordinatesComponent in parallelCoordinatesComponents.values) {
            parallelCoordinatesComponent.createOverplots()

            parallelCoordinatesComponent.scheduleUpdate()
        }
    }

    fun refresh() {
        runLayout()

        for (parallelCoordinatesComponent in parallelCoordinatesComponents.values) {
            parallelCoordinatesComponent.clearCache()
            parallelCoordinatesComponent.scheduleUpdate()
        }
    }

    private fun reset() {
        if (model != null) {
            runLayout()

            for (parallelCoordinatesComponent in parallelCoordinatesComponents.values) {
                parallelCoordinatesComponent.reset()
                parallelCoordinatesComponent.scheduleUpdate()
            }
        }
    }
}
